import React, { useMemo } from 'react';
import { Container, Row, Col, Card, Button, Accordion } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { SITE_PUBLIC_NAME } from '../data/legalCompanyInfo';
import PublicSiteNavbar from '../components/PublicSiteNavbar';
import PublicSiteFooter from '../components/PublicSiteFooter';
import SeoAlternateLinks from '../components/SeoAlternateLinks';
import StructuredData from '../components/seo/StructuredData';
import { LocalizedLink } from '../components/LocalizedLink';
import { useLocale } from '../contexts/LocaleContext';
import { SITE_ORIGIN, canonicalUrlForLocale } from '../utils/seoLocale';
import { buildPricingPageStructuredGraph } from '../utils/structuredDataSchemas';

const PRICING_PATH = '/tarifs/';

const PricingPage = () => {
  const { t } = useTranslation('pricing');
  const { locale } = useLocale();
  const canonical = canonicalUrlForLocale(locale, PRICING_PATH);
  const packs = t('packs', { returnObjects: true }) || [];
  const faqItems = t('faq.items', { returnObjects: true }) || [];

  const structuredData = useMemo(
    () =>
      buildPricingPageStructuredGraph({
        packs,
        faqItems,
        canonicalUrl: canonical,
        homeLabel: locale === 'en' ? 'Home' : 'Accueil',
        pricingLabel: locale === 'en' ? 'Pricing' : 'Tarifs',
        orgDescription: t('meta.description'),
        locale,
      }),
    [packs, faqItems, canonical, locale, t]
  );

  return (
    <>
      <Helmet htmlAttributes={{ lang: locale === 'en' ? 'en' : 'fr' }}>
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description')} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content={t('meta.title')} />
        <meta property="og:description" content={t('meta.description')} />
        <meta property="og:image" content={`${SITE_ORIGIN}/og-image.png`} />
        <meta property="og:site_name" content={SITE_PUBLIC_NAME} />
        <meta property="og:locale" content={locale === 'en' ? 'en_GB' : 'fr_FR'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonical} />
        <meta name="twitter:title" content={t('meta.title')} />
        <meta name="twitter:description" content={t('meta.description')} />
        <meta name="twitter:image" content={`${SITE_ORIGIN}/og-image.png`} />
      </Helmet>
      <StructuredData graph={structuredData} />
      <SeoAlternateLinks pathWithoutLocale={PRICING_PATH} />

      <PublicSiteNavbar />

      <div className="min-vh-100 d-flex flex-column bg-light pricing-page-shell">
        <Container className="py-4 py-md-5 flex-grow-1">
          <Row className="justify-content-center mb-5">
            <Col lg={8} className="text-center">
              <h1 className="display-5 fw-bold mb-3">{t('h1')}</h1>
              <p className="lead text-muted mb-2">{t('subtitle')}</p>
              <p className="text-secondary mb-0">{t('noSubscription')}</p>
            </Col>
          </Row>

          <Row className="g-4 justify-content-center mb-5">
            {packs.map((pack) => (
              <Col xs={12} md={6} lg={4} key={pack.sku}>
                <Card
                  className={`h-100 shadow-sm border-0 ${pack.popular ? 'border border-primary border-2' : ''}`}
                >
                  {pack.popular ? (
                    <div className="text-center">
                      <span className="badge bg-primary rounded-pill mt-3 px-3 py-2">{t('popular')}</span>
                    </div>
                  ) : null}
                  <Card.Body className="p-3 p-md-4 text-center d-flex flex-column">
                    <h2 className="h4 fw-bold mb-1">{pack.name}</h2>
                    <p className="text-muted small mb-3">{pack.hint}</p>
                    <div className="display-6 fw-bold text-primary mb-1 pricing-price">
                      {pack.priceDisplay}{' '}
                      <span className="fs-6 fw-normal text-muted">{t('taxSuffix')}</span>
                    </div>
                    <p className="text-muted small mb-4">{pack.per}</p>
                    <Button
                      as={LocalizedLink}
                      to="/register/"
                      variant={pack.popular ? 'primary' : 'outline-primary'}
                      className="mt-auto fw-semibold"
                    >
                      {locale === 'en' ? 'Get started' : 'Commencer'}
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          <Row className="justify-content-center mb-4">
            <Col lg={8} className="text-center">
              <p className="text-muted small mb-0">{t('taxNote')}</p>
            </Col>
          </Row>

          <Row className="justify-content-center mb-5">
            <Col lg={8}>
              <h2 className="h5 fw-bold text-center mb-3">{t('included.title')}</h2>
              <ul className="list-unstyled text-start">
                {(t('included.items', { returnObjects: true }) || []).map((item, i) => (
                  <li key={i} className="mb-2 d-flex align-items-start">
                    <span className="text-success me-2">&#10003;</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Col>
          </Row>

          <Row className="justify-content-center mb-5">
            <Col lg={8} className="text-center">
              <p className="text-muted small mb-0">
                {t('cgvPrefix')}{' '}
                <LocalizedLink to="/legal/cgv/" className="text-decoration-none">
                  {t('cgvLink')}
                </LocalizedLink>
                .
              </p>
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col lg={8}>
              <h2 className="h4 fw-bold text-center mb-4">{t('faq.title')}</h2>
              <Accordion defaultActiveKey="0">
                {faqItems.map((item, i) => (
                  <Accordion.Item eventKey={String(i)} key={item.q}>
                    <Accordion.Header>
                      <strong>{item.q}</strong>
                    </Accordion.Header>
                    <Accordion.Body style={{ whiteSpace: 'pre-line' }}>{item.a}</Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Col>
          </Row>
        </Container>

        <PublicSiteFooter />
      </div>
    </>
  );
};

export default PricingPage;
